import { useState } from "react"
import { useParams, Link } from "react-router-dom"
import { useQuery, useMutation } from "urql"
import { RotateCcw } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { EmptyState } from "@/components/shared/empty-state"
import { ProgressRing } from "@/components/shared/progress-ring"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { AttemptsTimeline } from "@/components/shared/attempts-timeline"
import { DeliveryQuery } from "@/lib/graphql/queries"
import { ForceRetryDeliveryMutation } from "@/lib/graphql/mutations"

export function DeliveryDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [result, reexecute] = useQuery({ query: DeliveryQuery, variables: { id: id! } })
  const [, forceRetry] = useMutation(ForceRetryDeliveryMutation)
  const [retrying, setRetrying] = useState(false)

  const handleForceRetry = async () => {
    setRetrying(true)
    const res = await forceRetry({ id: id! })
    setRetrying(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Delivery queued for retry")
    reexecute({ requestPolicy: "network-only" })
  }

  const delivery = result.data?.delivery

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!delivery) return <EmptyState title="Delivery not found" />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Delivery Detail</h1>
          <StatusBadge status={delivery.status} />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleForceRetry} disabled={retrying || delivery.status === "DELIVERED"}>
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            {retrying ? "Retrying..." : "Force Retry"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">ID</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{delivery.id}</code>
                <CopyButton text={delivery.id} />
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Event</FieldLabel>
              <Link to={`/events/${delivery.event.id}`} className="text-xs hover:underline">
                <code className="font-mono">{delivery.event.eventType}</code>
              </Link>
            </div>
            <div>
              <FieldLabel className="mb-1">Source</FieldLabel>
              <Link to={`/sources/${delivery.event.source.id}`} className="text-xs hover:underline">
                {delivery.event.source.name}
              </Link>
            </div>
            <div>
              <FieldLabel className="mb-1">Destination</FieldLabel>
              <Link to={`/destinations/${delivery.destination.id}`} className="text-xs hover:underline">
                {delivery.destination.name}
              </Link>
            </div>
            <div>
              <FieldLabel className="mb-1">Subscription</FieldLabel>
              <span className="text-xs">{delivery.subscription.name}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="flex items-center gap-4">
              <ProgressRing value={delivery.attempts} max={delivery.maxAttempts} />
              <div>
                <FieldLabel>Attempts</FieldLabel>
                <div className="font-medium tabular-nums">{delivery.attempts} / {delivery.maxAttempts}</div>
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Next Attempt</FieldLabel>
              <div>
                {delivery.nextAttemptAt ? (
                  <TimeAgo date={delivery.nextAttemptAt} />
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={delivery.createdAt} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <h2 className={sectionTitleClass}>Attempts Timeline</h2>
        <AttemptsTimeline attempts={delivery.deliveryAttempts} />
      </div>
    </div>
  )
}
